import { Link } from "react-router-dom";
import { Container, Logo } from "@/components/site/Bits";
import { useSite } from "@/lib/SiteContext";
import { Mail, Phone, MessageCircle } from "lucide-react";

const SERVICE_LINKS = [
  ["Damp surveys", "/surveys"],
  ["Damp proofing", "/services/damp-proofing"],
  ["Damp investigations", "/services/damp-investigations"],
  ["Rising damp assessments", "/services/rising-damp"],
  ["Penetrating damp", "/services/penetrating-damp"],
  ["Condensation control", "/services/condensation"],
  ["Mould remediation", "/services/mould-remediation"],
  ["Timber preservation", "/services/timber-preservation"],
];

const TOOL_LINKS = [
  ["Check My Damp", "/tools/check-my-damp"],
  ["Upload My Survey Report", "/tools/upload-my-survey"],
  ["Request an Estimate", "/tools/get-an-estimate"],
  ["Book an Inspection", "/book-an-inspection"],
  ["Payment Plans", "/payment-plans"],
  ["Customer Reviews", "/reviews"],
];

const AREA_LINKS = [
  ["Bedfordshire", "/areas-covered/bedfordshire"],
  ["Cambridgeshire", "/areas-covered/cambridgeshire"],
  ["Hertfordshire", "/areas-covered/hertfordshire"],
  ["Milton Keynes", "/areas-covered/milton-keynes"],
  ["Northamptonshire", "/areas-covered/northamptonshire"],
  ["Peterborough", "/areas-covered/peterborough"],
];

export default function SiteFooter() {
  const { settings } = useSite();
  const phone = settings?.telephone || "0333 335 6152";
  const email = settings?.email || "info@procuregroup.uk";
  const whatsapp = settings?.whatsapp || "0333 335 6152";

  return (
    <footer className="border-t border-pg-border bg-pg-pale" data-testid="site-footer">
      <Container className="py-14 md:py-16">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Logo showName={false} testId="footer-logo" />
            <p className="mt-4 text-sm text-pg-muted leading-relaxed">
              Procure Group Ltd — evidence-led property investigations and proportionate remedial
              recommendations across Bedfordshire, Cambridgeshire, Hertfordshire, Milton Keynes,
              Northamptonshire and Peterborough.
            </p>
            <div className="mt-5 space-y-1 text-sm sm:space-y-2">
              <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex min-h-[44px] items-center gap-2 pg-link md:min-h-0" data-testid="footer-phone">
                <Phone className="h-4 w-4" /> {phone}
              </a>
              <a href={`mailto:${email}`} className="flex min-h-[44px] items-center gap-2 pg-link md:min-h-0" data-testid="footer-email">
                <Mail className="h-4 w-4" /> {email}
              </a>
              <a
                href={`https://wa.me/44${whatsapp.replace(/^0/, "").replace(/\s/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="flex min-h-[44px] items-center gap-2 pg-link md:min-h-0"
                data-testid="footer-whatsapp"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp us
              </a>
            </div>
          </div>

          {[
            ["Services", SERVICE_LINKS],
            ["Online tools", TOOL_LINKS],
            ["Areas covered", AREA_LINKS],
          ].map(([title, links]) => (
            <div key={title}>
              <h3 className="text-sm font-semibold text-pg-dark">{title}</h3>
              <ul className="mt-2 space-y-0.5 sm:mt-4 sm:space-y-2.5">
                {links.map(([label, to]) => (
                  <li key={to}>
                    <Link to={to} className="pg-tap text-sm text-pg-muted hover:text-pg-primary transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-pg-border pt-6 text-xs text-pg-muted md:flex-row md:items-center md:justify-between">
          <div className="space-y-2">
            <p>© {new Date().getFullYear()} Procure Group Ltd. All rights reserved.</p>
            <p className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <Link to="/privacy-policy" className="pg-tap hover:text-pg-primary transition-colors" data-testid="footer-privacy">
                Privacy Policy
              </Link>
              <Link to="/cookie-policy" className="pg-tap hover:text-pg-primary transition-colors" data-testid="footer-cookie-policy">
                Cookie Policy
              </Link>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("pg:cookie-settings"))}
                className="pg-tap underline-offset-2 hover:text-pg-primary hover:underline transition-colors"
                data-testid="footer-cookie-settings"
              >
                Cookie settings
              </button>
            </p>
          </div>
          <p className="max-w-xl">
            Our online tools provide preliminary guidance only and do not replace a physical
            inspection. Where we may also carry out recommended works, we state that plainly.
          </p>
        </div>
      </Container>
    </footer>
  );
}
